import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link } from "react-router-dom";
import {
  CloseOutlined,
  InfoCircleOutlined,
  RightOutlined
} from "@ant-design/icons";
import { Popover, Tooltip } from "antd";
import type { Dayjs } from "dayjs";
import CIQDatePicker from "components/custom-date-picker";
import { bidPackageDetailsPath } from "pages/planning-workspace/bid-package-path";
import LinkIcon from "components/svg-icons/link-icon";
import {
  MaterialCiqIcon,
  ScheduleCiqIcon,
  SubmittalCiqIcon
} from "components/svg-icons";
import { TAuditChangeLog } from "change-events/change-event-polling";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import {
  LeadTimeLine,
  SiteMaterialLink,
  SiteSubmittalLink
} from "pages/design-package-details-page/components/view-dependencies/model";
import { QUERY_BID_PACKAGE_REQUIRED_ON_SITE } from "./required-on-site-query";
import {
  LinkedActivity,
  RawLinkedBidPackage,
  resolveLinkedEntities
} from "./resolve-linked-entities";
import {
  RawOnSiteBidPackage,
  resolveRequiredOnSite
} from "./resolve-required-on-site";

type LinkTab = "activity" | "material" | "submittal";

function siteDate(value: string | null | undefined): string {
  if (!value) return "";
  return DateUtils.format(value, DATE_FORMAT_MMDDYYYY);
}

/** Every id in the query response: the bid package, its links and entities. */
function collectIds(value: unknown, ids: Set<string>): Set<string> {
  if (Array.isArray(value)) {
    value.forEach((item) => collectIds(item, ids));
  } else if (value && typeof value === "object") {
    Object.entries(value).forEach(([key, child]) => {
      if (key === "id" && typeof child === "string") ids.add(child);
      else collectIds(child, ids);
    });
  }
  return ids;
}

/** Ids an audit row names, the way the planner reads them. */
function eventIds(log: TAuditChangeLog): string[] {
  return [
    ...(log.info?.feature_instance_ids ?? []),
    ...(log.info?.material_ids ?? []),
    ...(log.info?.submittal_ids ?? []),
    ...(log.info?.linked_features ?? []).map((feature) => feature.id),
    log.object_id
  ].filter((id): id is string => Boolean(id));
}

function CreatePlanLink(props: {
  projectId?: string;
  bidPackageId: string;
  className?: string;
}) {
  const { projectId, bidPackageId, className } = props;
  const label = "+ Add On-site Date";
  if (!projectId) {
    return <span className={className}>{label}</span>;
  }
  return (
    <Link
      to={bidPackageDetailsPath(projectId, bidPackageId)}
      data-testid={`planner-create-plan-${bidPackageId}`}
      className={className}
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  );
}

function daysText(days: number): string {
  return `${days} ${Math.abs(days) === 1 ? "day" : "days"}`;
}

function OnSiteHoverCard(props: {
  title: string;
  lines: LeadTimeLine[];
  total: number;
  /** No linked activities, materials or submittals, so no "View All Links". */
  hasLinks: boolean;
  onViewLinks: () => void;
}) {
  const { title, lines, total, hasLinks, onViewLinks } = props;
  return (
    <div
      data-testid="planner-lead-time-card"
      className="w-80 bg-white p-3 text-[13px] leading-5 text-ciq-gray-800"
    >
      <div className="mb-2 p-1 flex items-center justify-between gap-3">
        <span className="min-w-0 truncate text-sm font-semibold leading-[22px] text-ciq-gray-960">
          {title}
        </span>
        {hasLinks ? (
          <button
            type="button"
            data-testid="planner-ros-view-links"
            className="inline-flex shrink-0 cursor-pointer items-center gap-1 border-none bg-transparent p-0 !text-xs font-normal leading-5 text-ciq-planner-accent"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onViewLinks();
            }}
          >
            View All Links
            <RightOutlined className="text-[10px]" />
          </button>
        ) : null}
      </div>
      {lines.length || total > 0 ? (
        <div className="overflow-hidden rounded-[6px] border border-solid border-ciq-gray-100">
          {lines.map((line, index) =>
            line.section ? (
              <div
                key={`${line.label}-${index}`}
                className="bg-ciq-gray-50 px-3 py-2 text-xs font-medium text-ciq-gray-950"
              >
                {line.label}
              </div>
            ) : (
              <div
                key={`${line.label}-${index}`}
                className="flex items-center justify-between px-3 py-2"
              >
                <span>{line.label}</span>
                <span className="text-ciq-gray-960">{daysText(line.days)}</span>
              </div>
            )
          )}
          {total > 0 ? (
            <div className="flex items-center justify-between bg-ciq-primary-bg px-3 py-2 font-semibold text-ciq-gray-950">
              <span>Lead Time</span>
              <span>{daysText(total)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "*" marks the governing entity named on the Required on Site card. The
 * asterisk glyph sits near the cap height, so it is nudged down to the
 * middle of the text.
 */
function GoverningName(props: {
  id: string;
  name: string;
  governingId: string | null;
}) {
  const { id, name, governingId } = props;
  const governing = !!governingId && id === governingId;
  return (
    <span className="flex min-w-0 items-center gap-1">
      {governing ? (
        <span
          data-testid={`planner-governing-mark-${id}`}
          className="relative top-[0.2em] shrink-0 leading-none"
        >
          *
        </span>
      ) : null}
      <span className="min-w-0 truncate">{name}</span>
    </span>
  );
}

type EntityRow = {
  id: string;
  name: string;
  date: string | null;
  /** Names the date, e.g. "Start Date"; shown before it when set. */
  dateLabel?: string | null;
};

/** Link glyph before each linked entity; recoloured from its fixed grey. */
function LinkMark(props: { className?: string }) {
  const { className = "[&_path]:fill-ciq-planner-accent" } = props;
  return (
    <span
      aria-hidden
      className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5 [&_path]:[fill-opacity:1] ${className}`}
    >
      <LinkIcon />
    </span>
  );
}

function EntityCard(props: {
  id: string;
  title: string;
  rows: EntityRow[];
  governingId: string | null;
  /** Show an info icon whose tooltip names the entity. */
  showInfo?: boolean;
}) {
  const { id, title, rows, governingId, showInfo = false } = props;
  return (
    <div className="rounded-md border border-solid border-ciq-gray-200 bg-white px-2 py-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[13px] leading-5 text-ciq-gray-960">
          <LinkMark />
          <GoverningName id={id} name={title} governingId={governingId} />
        </span>
        {showInfo && title ? (
          <Tooltip title={title}>
            <InfoCircleOutlined className="shrink-0 text-xs text-ciq-planner-accent" />
          </Tooltip>
        ) : null}
      </div>
      {rows.map((item) => (
        <div
          key={item.id}
          className="mt-1 flex items-center justify-between gap-2 pl-1.5 text-[13px] leading-5 text-ciq-gray-500"
        >
          <span className="flex min-w-0 items-center gap-1">
            <GoverningName
              id={item.id}
              name={item.name}
              governingId={governingId}
            />
          </span>
          {item.date ? (
            <span className="shrink-0 text-xs">
              {item.dateLabel ? `${item.dateLabel}: ` : ""}
              {siteDate(item.date)}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function LinkedEntitiesPanel(props: {
  initial: LinkTab;
  /** Bid package name shown after "Linked Entities". */
  bidPackageName: string;
  activities: LinkedActivity[];
  materials: SiteMaterialLink[];
  submittals: SiteSubmittalLink[];
  /** Entity named on the card; marked "* " wherever it is listed. */
  governingId: string | null;
  onClose: () => void;
}) {
  const {
    initial,
    bidPackageName,
    activities,
    materials,
    submittals,
    governingId,
    onClose
  } = props;
  const [tab, setTab] = useState<LinkTab>(initial);
  const tabs: Array<{ id: LinkTab; label: string; count: number }> = [
    { id: "activity", label: "Activity", count: activities.length },
    { id: "material", label: "Material", count: materials.length },
    { id: "submittal", label: "Submittals", count: submittals.length }
  ];
  const heading = bidPackageName
    ? `Linked Entities ${bidPackageName}`
    : "Linked Entities";
  return (
    // The popover is portalled, but React still bubbles its wheel events to
    // the Required on Site list, which forwards them to the timeline rows.
    <div
      data-testid="planner-linked-entities"
      className="w-[420px] min-h-48"
      onWheel={(event) => event.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-ciq-gray-200 px-4 py-3">
        <span
          className="min-w-0 truncate text-sm text-ciq-gray-600"
          title={heading}
        >
          Linked Entities
          {bidPackageName ? (
            <span className="ml-1 font-medium">( {bidPackageName} )</span>
          ) : null}
        </span>
        <button
          type="button"
          aria-label="Close linked entities"
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-ciq-gray-500"
          onClick={onClose}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="flex gap-4 px-4 max-h-96 overflow-y-auto overscroll-contain">
        {tabs.map((item) => {
          const selected = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              className={`inline-flex cursor-pointer items-center gap-1.5 border-0 border-b-2 border-solid bg-transparent px-0 py-2 text-[13px] ${
                selected
                  ? "border-ciq-planner-accent font-medium text-ciq-planner-accent"
                  : "border-transparent text-ciq-gray-960"
              }`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              <span
                className={`inline-flex items-center gap-0.5 ${
                  selected ? "" : "text-ciq-gray-500"
                }`}
              >
                <LinkMark
                  className={
                    selected
                      ? "[&_path]:fill-ciq-planner-accent"
                      : "[&_path]:fill-ciq-gray-400"
                  }
                />
                {item.count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mx-3 mb-3 mt-2 flex max-h-80 flex-col gap-2 overflow-y-auto overscroll-contain rounded-md bg-ciq-gray-50 p-2">
        {tab === "activity"
          ? activities.map((activity) => (
              <EntityCard
                key={activity.id}
                id={activity.id}
                governingId={governingId}
                title={activity.name}
                showInfo
                rows={
                  activity.materialName
                    ? [
                        {
                          id: `${activity.id}-meta`,
                          name: activity.materialName,
                          date: activity.date,
                          dateLabel: activity.dateLabel
                        }
                      ]
                    : activity.date
                      ? [
                          {
                            id: `${activity.id}-date`,
                            name: "",
                            date: activity.date,
                            dateLabel: activity.dateLabel
                          }
                        ]
                      : []
                }
              />
            ))
          : null}
        {/* One level only: the Material and Submittal tabs list those
            entities, not what is linked to them. */}
        {tab === "material"
          ? materials.map((material) => (
              <EntityCard
                key={material.id}
                id={material.id}
                governingId={governingId}
                title={material.title}
                rows={[]}
              />
            ))
          : null}
        {tab === "submittal"
          ? submittals.map((submittal) => (
              <EntityCard
                key={submittal.id}
                id={submittal.id}
                governingId={governingId}
                title={submittal.title}
                rows={[]}
              />
            ))
          : null}
      </div>
    </div>
  );
}

/**
 * Required on Site card for one bid package. The governing entity, its date
 * and the lead-time breakdown, and the link counts and "View All Links" lists
 * come from one query by bid package id.
 */
export default function BidPackageRequiredOnSite(props: {
  bidPackageId: string;
  projectId?: string;
  /** No milestone dates yet, so the card offers "+ Add On-site Date". */
  unplanned?: boolean;
  /** High risk or running late: the card border reads light red. */
  alert?: boolean;
  onSetDate?: (isoDate: string | null) => void | Promise<void>;
}) {
  const {
    bidPackageId,
    projectId,
    unplanned = false,
    alert = false,
    onSetDate
  } = props;
  const { gqlClientForProject, eventLogs } = useContext(ProjectContext);
  const { data, loading, refetch } = useCIQQuery<{
    feature_instance: Array<RawOnSiteBidPackage & RawLinkedBidPackage>;
  }>(QUERY_BID_PACKAGE_REQUIRED_ON_SITE, {
    client: gqlClientForProject,
    variables: { bidPackageIds: [bidPackageId] },
    skip: !gqlClientForProject || !bidPackageId,
    fetchPolicy: "cache-and-network"
  });
  const onSite = useMemo(
    () => resolveRequiredOnSite(data?.feature_instance?.[0]),
    [data]
  );

  // Linking or unlinking an entity logs the ids on both ends, so a row naming
  // the bid package or anything already linked to it reloads the card.
  const linkedIds = useMemo(
    () => collectIds(data?.feature_instance, new Set([bidPackageId])),
    [data, bidPackageId]
  );
  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (!eventLogs?.length || previousEventLogs.current === eventLogs) return;
    previousEventLogs.current = eventLogs;
    const affected = eventLogs.some((log) =>
      eventIds(log).some((id) => linkedIds.has(id))
    );
    if (affected) refetch?.();
  }, [eventLogs, linkedIds, refetch]);

  const [picking, setPicking] = useState(false);
  const [hot, setHot] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  // Clicking "View All Links" swaps in a taller panel. The cursor is then
  // outside the repositioned hover target, so ignore leave until it is closed.
  const linksHold = useRef(false);

  const { activities, materials, submittals } = useMemo(
    () => resolveLinkedEntities(data?.feature_instance?.[0]),
    [data]
  );

  const activityCount = activities.length;
  const materialCount = materials.length;
  const submittalCount = submittals.length;
  const showCounts =
    activityCount > 0 || materialCount > 0 || submittalCount > 0;
  const loadingFirst = loading && !data;
  const notLinked = !loadingFirst && !onSite && !showCounts;
  const showCreatePlan = !loadingFirst && !onSite && showCounts && unplanned;
  const ownDeadline = onSite?.ownDeadline ?? false;
  const total = onSite?.totalDays ?? 0;
  const showLead = !!onSite && !ownDeadline && total > 0;
  const detail = onSite ? `${onSite.dateLabel} ${siteDate(onSite.date)}` : "";

  const setDate = async (isoDate: string | null) => {
    setPicking(false);
    await onSetDate?.(isoDate);
    refetch?.();
  };

  const countMark = (testId: string, count: number, icon: ReactNode) => {
    if (count <= 0) return null;
    return (
      <span
        data-testid={testId}
        className={`inline-flex items-center gap-1 text-[11px] leading-4 ${
          hot ? "text-ciq-planner-ros" : "text-ciq-planner-ink2"
        }`}
      >
        {icon}
        {count}
      </span>
    );
  };
  const iconClass = hot ? "fill-ciq-planner-ros" : "fill-black";
  const linksTab: LinkTab = materials.length
    ? "material"
    : submittals.length
      ? "submittal"
      : "activity";
  const cardBorder = hot
    ? "border-ciq-planner-ros"
    : alert
      ? "border-ciq-red-200"
      : "border-ciq-gray-300";

  let content: ReactNode;
  if (loadingFirst) {
    content = (
      <div
        data-testid={`planner-ros-loading-${bidPackageId}`}
        className="truncate text-[11px] leading-4 text-ciq-gray-500"
      >
        Loading…
      </div>
    );
  } else if (notLinked) {
    content = (
      <div className="min-w-0">
        <div
          data-testid={`planner-ros-unlinked-${bidPackageId}`}
          className="truncate text-[13px] font-semibold leading-4 text-ciq-planner-ink4"
        >
          Not linked to an Activity
        </div>
      </div>
    );
  } else if (showCreatePlan) {
    content = (
      <div className="min-w-0">
        <div className="truncate text-[13px] leading-4 text-ciq-planner-ink4">
          No date yet
        </div>
        <CreatePlanLink
          projectId={projectId}
          bidPackageId={bidPackageId}
          className="truncate text-[11px] font-medium leading-4 text-ciq-planner-accent"
        />
      </div>
    );
  } else if (onSite) {
    content = (
      <div className="min-w-0">
        {onSite.title ? (
          <div
            data-testid={`planner-ros-title-${bidPackageId}`}
            className="truncate text-xs font-semibold leading-4 text-ciq-gray-960"
            title={onSite.title}
          >
            {onSite.title}
          </div>
        ) : null}
        <div
          data-testid={`planner-ros-date-${bidPackageId}`}
          className="truncate text-[11px] font-normal leading-4 text-ciq-planner-ink3"
        >
          {detail}
        </div>
      </div>
    );
  } else {
    content = (
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold leading-4 text-ciq-gray-950">
          Bid Package Deadline
        </div>
        {onSetDate ? (
          picking ? (
            <CIQDatePicker
              open
              size="small"
              className="h-7 w-[148px]"
              placeholder="Select date"
              format={DATE_FORMAT_MMDDYYYY}
              value={null}
              placement="topLeft"
              getPopupContainer={() => document.body}
              data-testid={`planner-ros-date-${bidPackageId}`}
              onOpenChange={(open) => {
                if (!open) setPicking(false);
              }}
              onChange={(date: Dayjs | null) => {
                setDate(date ? DateUtils.formatDateWithLunchTime(date) : null);
              }}
            />
          ) : (
            <button
              type="button"
              data-testid={`planner-ros-add-date-${bidPackageId}`}
              className="cursor-pointer border-none bg-transparent p-0 text-xs font-medium leading-4 text-ciq-planner-accent"
              onClick={() => setPicking(true)}
            >
              Select date
            </button>
          )
        ) : null}
      </div>
    );
  }

  const cardBody = (
    <div
      data-testid={`planner-ros-hover-${bidPackageId}`}
      className="flex min-w-0 flex-col"
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
    >
      <div className="mb-px flex h-[18px] items-center justify-between gap-2">
        {showLead ? (
          <span
            data-testid={`planner-ros-lead-${bidPackageId}`}
            className="inline-flex items-center rounded border border-solid border-ciq-gray-300 bg-ciq-black-a2 px-2 text-xs leading-4 text-ciq-black-a88"
          >
            {total} days
          </span>
        ) : (
          <span />
        )}
        {showCounts ? (
          <div className="flex items-center gap-2">
            {countMark(
              `planner-ros-activities-${bidPackageId}`,
              activityCount,
              <ScheduleCiqIcon size={14} className={iconClass} opacity="1" />
            )}
            {countMark(
              `planner-ros-materials-${bidPackageId}`,
              materialCount,
              <MaterialCiqIcon size={14} className={iconClass} opacity="1" />
            )}
            {countMark(
              `planner-ros-submittals-${bidPackageId}`,
              submittalCount,
              <SubmittalCiqIcon size={14} className={iconClass} opacity="1" />
            )}
          </div>
        ) : null}
      </div>
      <div
        data-testid={`planner-ros-card-${bidPackageId}`}
        className={`relative flex h-[46px] min-w-0 items-center gap-2 bg-white px-2 ${
          onSite
            ? "rounded-md border border-solid"
            : "rounded-md border border-dashed"
        } ${cardBorder}`}
      >
        <span
          aria-hidden
          className={`absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-0 border-b border-l border-solid bg-white ${cardBorder}`}
        />
        {content}
      </div>
    </div>
  );

  if (loadingFirst || notLinked || showCreatePlan || ownDeadline) {
    return cardBody;
  }

  return (
    <Popover
      trigger="hover"
      placement="leftTop"
      arrow={false}
      mouseLeaveDelay={0.2}
      open={linksOpen || hoverOpen}
      styles={{
        container: { padding: 0, minHeight: linksOpen ? 280 : undefined }
      }}
      onOpenChange={(next) => {
        if (!next && linksHold.current) return;
        setHoverOpen(next);
        if (!next) {
          linksHold.current = false;
          setLinksOpen(false);
        }
      }}
      content={
        linksOpen ? (
          <LinkedEntitiesPanel
            initial={linksTab}
            bidPackageName={data?.feature_instance?.[0]?.title?.trim() ?? ""}
            activities={activities}
            materials={materials}
            submittals={submittals}
            governingId={onSite?.governingId ?? null}
            onClose={() => {
              linksHold.current = false;
              setLinksOpen(false);
            }}
          />
        ) : (
          <OnSiteHoverCard
            title={onSite?.title || "Required on site"}
            lines={onSite?.leadLines ?? []}
            total={total}
            hasLinks={showCounts}
            onViewLinks={() => {
              linksHold.current = true;
              setLinksOpen(true);
              setHoverOpen(true);
            }}
          />
        )
      }
    >
      {cardBody}
    </Popover>
  );
}
